import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { getSupportMessengerProvider } from '@/lib/support/factory';
import { shouldBootSupportMessenger, shouldOfferGuestSupportButton } from '@/lib/support/gate';
import type { SupportAttributeValue, SupportMessengerProvider } from '@/lib/support/types';

interface SupportIdentityResponse {
  userJwt: string;
  /** Optional so an older support-identity deployment (userJwt only) still boots fine —
   *  attributes are additive, never required for a valid boot. */
  attributes?: Record<string, SupportAttributeValue>;
}

async function fetchSupportIdentity(): Promise<SupportIdentityResponse> {
  const { data, error } = await supabase.functions.invoke('support-identity');
  if (error) throw error;
  return data as SupportIdentityResponse;
}

export interface UseSupportMessenger {
  /** True once the widget has ACTUALLY booted for this session (identified OR guest) — not
   *  merely "we started trying to". Other components (e.g. the tenant<->owner conversation
   *  header's escalation button) read this to decide whether to offer a way into support; a
   *  widget still loading, or whose script failed (ad-blocker etc.), must never be reported as
   *  active. */
  isActive: boolean;
  /** Opens a fresh support conversation with an optional pre-filled composer. Resolves true
   *  once the widget reported it opened; false when it is not active or did not open in time. */
  openNewMessage: (prefill?: string) => Promise<boolean>;
  /** True when the discreet "Need help?" guest button should be offered right now (logged out,
   *  on an allow-listed page like /claim/:token, App ID configured, nothing booted yet). See
   *  src/lib/support/gate.ts's shouldOfferGuestSupportButton for the exact rule. */
  canOfferGuestHelp: boolean;
  /** Boots an anonymous session (no identity, no attributes) and opens the messenger window —
   *  the click IS the request to see it. No-op if the gate says no (already booted, logged in,
   *  off the allow-list, no App ID). Never called automatically. */
  bootGuestSession: () => void;
}

/**
 * Boots/shuts down the customer-support messenger in step with auth state + route.
 * Self-contained (tracks the Supabase auth session itself, like useStreamClient). Hidden on
 * /admin/* (operators aren't support customers) and torn down on logout OR on an in-place
 * session swap (authUserId changes without an intervening null — e.g. a shared-browser account
 * switch) so a session never leaks between accounts. Entirely a no-op when VITE_SUPPORT_APP_ID
 * isn't configured, or when the configured provider name is unrecognized (factory returns null).
 *
 * Also owns the separate GUEST session path (2026-07-21 decision, see the customer-support
 * handover doc's decisions): a logged-out visitor on an allow-listed page (e.g. /claim/:token)
 * can explicitly click a "Need help?" button to boot an anonymous session — never automatic,
 * no identity, no attributes. If that visitor later logs in, the guest session is torn down
 * BEFORE the identified boot (deliberate privacy separation: we never try to merge/upgrade a
 * guest lead into the identified account). Logging back out returns to the plain button state —
 * no auto-reboot of a fresh anonymous session.
 */
export function useSupportMessenger(): UseSupportMessenger {
  const appId = (import.meta.env.VITE_SUPPORT_APP_ID as string | undefined) ?? '';
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);
  const providerRef = useRef<SupportMessengerProvider | null>(null);
  // Separate from providerRef itself: the factory can legitimately RESOLVE to null (an
  // unrecognized VITE_SUPPORT_PROVIDER value), and providerRef.current staying null must not
  // be mistaken for "not attempted yet" — that would re-invoke the factory (and its
  // console.warn) on every single render forever (doubly so under StrictMode).
  const providerResolvedRef = useRef(false);
  // Which authUserId is currently booted (null = nobody). Deliberately NOT a bare boolean: an
  // in-place session swap (A -> B without a SIGNED_OUT/null in between) must still register as
  // "a different user", or the widget would keep running on A's JWT while B types into it.
  const bootedUserIdRef = useRef<string | null>(null);
  // Whether an anonymous GUEST session (no identity) is currently booted — tracked separately
  // from bootedUserIdRef because a guest session has no authUserId to key off at all.
  const guestBootedRef = useRef(false);
  // Bumped to invalidate any in-flight bootGuestSession() attempt (e.g. the visitor logs in
  // while the guest boot's load()/boot() is still pending) — the same "check after every await,
  // shutdown if invalidated" pattern as the identified path's cancellation fix.
  const guestBootGenerationRef = useRef(0);

  if (appId && !providerResolvedRef.current) {
    providerResolvedRef.current = true;
    providerRef.current = getSupportMessengerProvider(appId);
  }

  useEffect(() => {
    if (!appId) return;
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setAuthUserId(data.session?.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthUserId(session?.user?.id ?? null);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [appId]);

  useEffect(() => {
    if (!appId || !providerRef.current) return;
    const provider = providerRef.current;
    const shouldBoot = shouldBootSupportMessenger({
      isLoggedIn: !!authUserId,
      pathname,
      hasAppId: true,
    });

    if (!shouldBoot) {
      if (bootedUserIdRef.current !== null) {
        provider.shutdown();
        bootedUserIdRef.current = null;
        setIsActive(false);
      }
      return;
    }

    if (bootedUserIdRef.current === authUserId) return; // already booted for this exact user

    if (bootedUserIdRef.current !== null || guestBootedRef.current) {
      // Booted for a DIFFERENT user, OR a guest/anonymous session was active — tear down first.
      // A guest->identified transition is a deliberate privacy separation (2026-07-21 decision):
      // we never merge/upgrade an anonymous lead into the newly-identified account, just start
      // clean. Also invalidates any bootGuestSession() attempt still in flight.
      guestBootGenerationRef.current++;
      provider.shutdown();
      bootedUserIdRef.current = null;
      guestBootedRef.current = false;
      setIsActive(false);
    }

    let cancelled = false;
    (async () => {
      const { userJwt, attributes } = await fetchSupportIdentity();
      if (cancelled) return; // nothing loaded/booted yet — a plain return is enough here

      // load() is the slow step (the real CDN script fetch can take seconds) — split it out
      // from boot() so a cancellation that lands DURING that wait (logout, a route change into
      // /admin, an in-place session swap) is caught right after it resolves, before the boot
      // command itself is ever issued. Checking cancelled only after boot() (as before) is too
      // late: by then the widget's boot command (carrying the OLD user's JWT) has already been
      // sent to the provider — the launcher would keep running as the old user until a full
      // page reload.
      await provider.load();
      if (cancelled) {
        // Belt and suspenders: the script may already be sitting there with no identity —
        // shut it down rather than leaving it in limbo.
        provider.shutdown();
        return;
      }

      // No meaningful wait between here and the actual command: boot() re-awaits load()
      // internally, but that promise is already settled, so it resumes on the very next
      // microtask rather than reopening a real waiting window.
      await provider.boot({ userJwt, languageOverride: i18n.language, attributes });
      if (cancelled) {
        // Belt and suspenders: even that microtask-scale gap could in principle be hit — if it
        // was, undo the boot immediately instead of leaving the old user's session live.
        provider.shutdown();
        return;
      }

      // Only NOW — after the identity fetch AND the widget script have both succeeded — is the
      // widget genuinely usable. Setting this any earlier would show a "Need help?" escalation
      // button that silently does nothing if the CDN script never loaded.
      bootedUserIdRef.current = authUserId;
      setIsActive(true);
    })().catch((err) => {
      // Non-critical: the widget just stays absent this session. Never surface a
      // user-facing error for a support tool being unavailable.
      console.error('[support] failed to boot messenger', err);
    });

    return () => {
      cancelled = true;
    };
    // i18n.language is intentionally excluded: a language switch nudges the already-booted
    // widget via the effect below instead of forcing a full re-boot (new JWT fetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, authUserId, pathname]);

  useEffect(() => {
    if (!appId || bootedUserIdRef.current === null || !providerRef.current) return;
    providerRef.current.update({ languageOverride: i18n.language });
  }, [appId, i18n.language]);

  const openNewMessage = useCallback((prefill?: string) => {
    if ((bootedUserIdRef.current === null && !guestBootedRef.current) || !providerRef.current) return Promise.resolve(false);
    return providerRef.current.openNewMessage(prefill);
  }, []);

  const canOfferGuestHelp = shouldOfferGuestSupportButton({
    isLoggedIn: !!authUserId,
    pathname,
    hasAppId: !!appId,
    alreadyBooted: bootedUserIdRef.current !== null || guestBootedRef.current,
  });

  const bootGuestSession = useCallback(() => {
    const provider = providerRef.current;
    if (!provider) return;
    if (
      !shouldOfferGuestSupportButton({
        isLoggedIn: !!authUserId,
        pathname,
        hasAppId: !!appId,
        alreadyBooted: bootedUserIdRef.current !== null || guestBootedRef.current,
      })
    ) {
      return;
    }

    const generation = ++guestBootGenerationRef.current;
    const isInvalidated = () => guestBootGenerationRef.current !== generation;

    (async () => {
      // Same split-and-recheck pattern as the identified path: load() is the slow step, so a
      // login/navigation-away that invalidates this attempt mid-flight is caught right after it
      // resolves, before the boot command itself is ever issued.
      await provider.load();
      if (isInvalidated()) {
        provider.shutdown();
        return;
      }
      await provider.bootAnonymous({ languageOverride: i18n.language });
      if (isInvalidated()) {
        provider.shutdown();
        return;
      }
      guestBootedRef.current = true;
      setIsActive(true);
    })().catch((err) => {
      console.error('[support] failed to boot guest session', err);
    });
  }, [appId, authUserId, pathname, i18n.language]);

  return { isActive, openNewMessage, canOfferGuestHelp, bootGuestSession };
}
